import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { CardBox } from '@/components/ui/CardBox';
import { Icon } from '@/components/ui/Icon';
import { formatDate, formatHour, formatTimeAgo } from '@/lib/dateUtils';
import {
  mdiAccountGroup,
  mdiAlertCircle,
  mdiArrowDown,
  mdiArrowUp,
  mdiCalendarCheck,
  mdiCalendarClock,
  mdiChartBar,
  mdiClock,
  mdiLoading,
  mdiMapMarker,
  mdiRefresh,
  mdiStore,
  mdiTrendingUp,
} from '@mdi/js';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { DayOfWeekChart } from './components/DayOfWeekChart';
import { usePlatformAnalytics } from './hooks/usePlanAnalytics';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: string;
  change?: number;
  changeLabel?: string;
  isLoading?: boolean;
  variant?: 'default' | 'success' | 'warning';
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  change,
  changeLabel,
  isLoading,
  variant = 'default',
}) => {
  const hasChange = change !== undefined && change !== 0;
  const isPositive = change !== undefined && change > 0;

  const variantStyles = {
    default: '',
    success: 'bg-coffice-green/10',
    warning: 'bg-coffice-yellow/20',
  };

  return (
    <CardBox className={variantStyles[variant]}>
      <div className='flex items-center justify-between'>
        <div className='flex-1'>
          <p className='text-sm font-medium text-grey-600'>{title}</p>
          {isLoading ? (
            <div className='flex items-center mt-2'>
              <Icon
                path={mdiLoading}
                size={24}
                className='text-grey-400 animate-spin'
              />
            </div>
          ) : (
            <>
              <p className='text-3xl font-bold text-grey-900 mt-2'>{value}</p>
              {hasChange && (
                <div className='flex items-center mt-2'>
                  <Icon
                    path={isPositive ? mdiArrowUp : mdiArrowDown}
                    size={16}
                    className={
                      isPositive ? 'text-coffice-green' : 'text-red-500'
                    }
                  />
                  <span
                    className={`text-sm ml-1 ${isPositive ? 'text-coffice-green' : 'text-red-600'}`}
                  >
                    {Math.abs(change)}%
                  </span>
                  {changeLabel && (
                    <span className='text-sm text-grey-500 ml-1'>
                      {changeLabel}
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <div className='p-3 rounded-full bg-grey-50 flex items-center justify-center'>
          <Icon path={icon} size={24} className='text-grey-500' />
        </div>
      </div>
    </CardBox>
  );
};

interface MetricRowProps {
  label: string;
  value: string | number;
  isLoading?: boolean;
}

const MetricRow: React.FC<MetricRowProps> = ({ label, value, isLoading }) => (
  <div className='flex items-center justify-between py-3 border-b border-grey-100 last:border-b-0'>
    <span className='text-grey-600'>{label}</span>
    {isLoading ? (
      <Icon
        path={mdiLoading}
        size={20}
        className='text-grey-400 animate-spin'
      />
    ) : (
      <span className='text-xl font-bold text-grey-900'>{value}</span>
    )}
  </div>
);

interface PlanStateBadgeProps {
  state: 'upcoming' | 'now' | 'past';
}

const PlanStateBadge: React.FC<PlanStateBadgeProps> = ({ state }) => {
  const config = (() => {
    switch (state) {
      case 'upcoming':
        return {
          label: 'Upcoming',
          className: 'bg-grey-100 text-grey-600',
        };
      case 'now':
        return {
          label: 'Now',
          className: 'bg-coffice-yellow/30 text-grey-900',
        };
      case 'past':
        return {
          label: 'Past',
          className: 'bg-grey-100 text-grey-600',
        };
    }
  })();

  return (
    <span
      className={`px-2 py-0.5 text-xs font-medium rounded-full ${config.className}`}
    >
      {config.label}
    </span>
  );
};

export const AnalyticsPage: React.FC = () => {
  const { data, isLoading, refetch, isRefetching } = usePlatformAnalytics();

  return (
    <DashboardLayout>
      <ProtectedRoute action='read' subject='Analytics'>
        <div className='space-y-8'>
          {/* Header */}
          <div className='flex items-center justify-between'>
            <div>
              <h1 className='text-3xl font-bold text-grey-900'>
                Platform Analytics
              </h1>
              <p className='text-base text-grey-600 mt-2'>
                Track growth, engagement, and business performance
              </p>
            </div>
            <button
              onClick={() => refetch()}
              disabled={isRefetching}
              className='inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-grey-700 bg-white shadow-sm hover:shadow-md transition-shadow disabled:opacity-50'
            >
              <Icon
                path={mdiRefresh}
                size={18}
                className={`mr-2 ${isRefetching ? 'animate-spin' : ''}`}
              />
              Refresh
            </button>
          </div>

          {/* Growth Stats Grid */}
          <div>
            <h2 className='text-lg font-semibold text-grey-900 mb-4'>Growth</h2>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
              <StatCard
                title='Plans This Week'
                value={data?.growth.plansThisWeek.toLocaleString() || 0}
                icon={mdiCalendarCheck}
                change={data?.growth.weeklyGrowth}
                changeLabel='vs last week'
                isLoading={isLoading}
              />
              <StatCard
                title='Plans This Month'
                value={data?.growth.plansThisMonth.toLocaleString() || 0}
                icon={mdiChartBar}
                change={data?.growth.monthlyGrowth}
                changeLabel='vs last month'
                isLoading={isLoading}
              />
              <StatCard
                title='New Users This Week'
                value={data?.growth.newUsersThisWeek.toLocaleString() || 0}
                icon={mdiAccountGroup}
                isLoading={isLoading}
              />
              <StatCard
                title='New Businesses This Week'
                value={data?.growth.newBusinessesThisWeek.toLocaleString() || 0}
                icon={mdiStore}
                isLoading={isLoading}
              />
            </div>
          </div>

          {/* User Engagement and Plan Status Row */}
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
            {/* User Engagement */}
            <CardBox>
              <div className='flex items-center gap-3 mb-6'>
                <div className='p-2 rounded-full bg-grey-50'>
                  <Icon
                    path={mdiAccountGroup}
                    size={20}
                    className='text-grey-500'
                  />
                </div>
                <h3 className='text-lg font-semibold text-grey-900'>
                  User Engagement
                </h3>
              </div>
              <div className='space-y-1'>
                <MetricRow
                  label='Active Users (7 days)'
                  value={data?.engagement.activeUsers7d || 0}
                  isLoading={isLoading}
                />
                <MetricRow
                  label='Active Users (30 days)'
                  value={data?.engagement.activeUsers30d || 0}
                  isLoading={isLoading}
                />
                <MetricRow
                  label='Avg Plans/User (7d)'
                  value={data?.engagement.avgPlansPerUser7d || 0}
                  isLoading={isLoading}
                />
                <MetricRow
                  label='Repeat Users (2+ plans)'
                  value={data?.engagement.repeatUsers || 0}
                  isLoading={isLoading}
                />
                <MetricRow
                  label='Power Users (5+ plans/month)'
                  value={data?.engagement.powerUsers || 0}
                  isLoading={isLoading}
                />
              </div>
            </CardBox>

            {/* Plan Status */}
            <CardBox>
              <div className='flex items-center gap-3 mb-6'>
                <div className='p-2 rounded-full bg-grey-50'>
                  <Icon
                    path={mdiCalendarClock}
                    size={20}
                    className='text-grey-500'
                  />
                </div>
                <h3 className='text-lg font-semibold text-grey-900'>
                  Plan State
                </h3>
              </div>
              <div className='space-y-1'>
                <MetricRow
                  label='Upcoming'
                  value={data?.planState?.upcoming || 0}
                  isLoading={isLoading}
                />
                <MetricRow
                  label='Happening Now'
                  value={data?.planState?.now || 0}
                  isLoading={isLoading}
                />
                <MetricRow
                  label='Past'
                  value={data?.planState?.past || 0}
                  isLoading={isLoading}
                />
                <MetricRow
                  label='Avg Planned Duration'
                  value={`${data?.planState?.avgDurationHours || 0}h`}
                  isLoading={isLoading}
                />
              </div>
            </CardBox>
          </div>

          {/* Geographic Demand */}
          <CardBox>
            <div className='flex items-center gap-3 mb-6'>
              <div className='p-2 rounded-full bg-grey-50'>
                <Icon path={mdiMapMarker} size={20} className='text-grey-500' />
              </div>
              <h3 className='text-lg font-semibold text-grey-900'>
                Geographic Demand
              </h3>
            </div>
            {isLoading ? (
              <div className='flex items-center justify-center py-8'>
                <Icon
                  path={mdiLoading}
                  size={24}
                  className='text-grey-400 animate-spin'
                />
              </div>
            ) : data?.geographic && data.geographic.length > 0 ? (
              <div className='overflow-x-auto'>
                <table className='w-full'>
                  <thead>
                    <tr className='border-b border-grey-200'>
                      <th className='text-left py-3 px-4 text-sm font-medium text-grey-500'>
                        Rank
                      </th>
                      <th className='text-left py-3 px-4 text-sm font-medium text-grey-500'>
                        City
                      </th>
                      <th className='text-left py-3 px-4 text-sm font-medium text-grey-500'>
                        Country
                      </th>
                      <th className='text-right py-3 px-4 text-sm font-medium text-grey-500'>
                        Plans
                      </th>
                      <th className='text-right py-3 px-4 text-sm font-medium text-grey-500'>
                        Businesses
                      </th>
                      <th className='text-right py-3 px-4 text-sm font-medium text-grey-500'>
                        Plans/Business
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.geographic.map((geo, index) => (
                      <tr
                        key={`${geo.city}-${geo.country}`}
                        className='border-b border-grey-50 hover:bg-grey-50'
                      >
                        <td className='py-3 px-4 text-sm text-grey-400'>
                          #{index + 1}
                        </td>
                        <td className='py-3 px-4 font-medium text-grey-900'>
                          {geo.city}
                        </td>
                        <td className='py-3 px-4 text-grey-600'>
                          {geo.country}
                        </td>
                        <td className='py-3 px-4 text-right font-bold text-grey-900'>
                          {geo.planCount}
                        </td>
                        <td className='py-3 px-4 text-right text-grey-600'>
                          {geo.businessCount}
                        </td>
                        <td className='py-3 px-4 text-right text-grey-600'>
                          {geo.plansPerBusiness}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className='text-grey-500 text-center py-8'>
                No geographic data yet
              </p>
            )}
          </CardBox>

          {/* Top Businesses and Booking Patterns Row */}
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
            {/* Top Businesses */}
            <CardBox>
              <div className='flex items-center justify-between mb-4'>
                <div className='flex items-center gap-3'>
                  <div className='p-2 rounded-full bg-grey-50'>
                    <Icon path={mdiStore} size={20} className='text-grey-500' />
                  </div>
                  <h3 className='text-lg font-semibold text-grey-900'>
                    Top Businesses
                  </h3>
                </div>
                <Link
                  to='/businesses'
                  className='text-sm text-coffice-blue hover:text-coffice-blue/80'
                >
                  View all
                </Link>
              </div>
              {isLoading ? (
                <div className='flex items-center justify-center py-8'>
                  <Icon
                    path={mdiLoading}
                    size={24}
                    className='text-grey-400 animate-spin'
                  />
                </div>
              ) : data?.topBusinesses && data.topBusinesses.length > 0 ? (
                <div className='space-y-3'>
                  {data.topBusinesses.slice(0, 5).map((business, index) => (
                    <Link
                      key={business.id}
                      to={`/businesses/${business.id}`}
                      className='flex items-center justify-between py-3 hover:bg-grey-50 -mx-2 px-2 rounded'
                    >
                      <div className='flex items-center gap-3'>
                        <span className='text-sm font-medium text-grey-400 w-6'>
                          #{index + 1}
                        </span>
                        <div>
                          <p className='font-medium text-grey-900'>
                            {business.name}
                          </p>
                          {business.city && (
                            <p className='text-sm text-grey-500'>
                              {business.city}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className='text-right'>
                        <p className='font-bold text-grey-900'>
                          {business.planCount}
                        </p>
                        <p className='text-xs text-grey-500'>plans</p>
                      </div>
                    </Link>
                  ))}
                  {data.businessesWithNoPlans > 0 && (
                    <div className='flex items-center gap-2 mt-4 pt-4 border-t border-grey-100'>
                      <Icon
                        path={mdiAlertCircle}
                        size={16}
                        className='text-amber-500'
                      />
                      <span className='text-sm text-amber-700'>
                        {data.businessesWithNoPlans} businesses with 0 plans
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <p className='text-grey-500 text-center py-8'>
                  No booking data yet
                </p>
              )}
            </CardBox>

            {/* Booking Patterns */}
            <CardBox>
              <div className='flex items-center gap-3 mb-6'>
                <div className='p-2 rounded-full bg-grey-50'>
                  <Icon path={mdiClock} size={20} className='text-grey-500' />
                </div>
                <h3 className='text-lg font-semibold text-grey-900'>
                  Booking Patterns
                </h3>
              </div>
              {isLoading ? (
                <div className='flex items-center justify-center py-8'>
                  <Icon
                    path={mdiLoading}
                    size={24}
                    className='text-grey-400 animate-spin'
                  />
                </div>
              ) : data?.bookingPatterns ? (
                <div>
                  <DayOfWeekChart data={data.bookingPatterns.byDayOfWeek} />
                  <div className='grid grid-cols-2 gap-4 mt-6'>
                    <div className='text-center p-3 bg-grey-50 rounded-lg'>
                      <p className='text-2xl font-bold text-grey-900'>
                        {data.bookingPatterns.avgDaysInAdvance}
                      </p>
                      <p className='text-xs text-grey-500'>
                        Avg days in advance
                      </p>
                    </div>
                    <div className='text-center p-3 bg-grey-50 rounded-lg'>
                      <p className='text-2xl font-bold text-grey-900'>
                        {data.bookingPatterns.sameDayPercentage}%
                      </p>
                      <p className='text-xs text-grey-500'>Same-day bookings</p>
                    </div>
                    <div className='text-center p-3 bg-grey-50 rounded-lg'>
                      <p className='text-2xl font-bold text-grey-900'>
                        {data.bookingPatterns.peakDay}
                      </p>
                      <p className='text-xs text-grey-500'>Peak day</p>
                    </div>
                    <div className='text-center p-3 bg-grey-50 rounded-lg'>
                      <p className='text-2xl font-bold text-grey-900'>
                        {formatHour(data.bookingPatterns.peakHour)}
                      </p>
                      <p className='text-xs text-grey-500'>Peak hour</p>
                    </div>
                  </div>
                </div>
              ) : (
                <p className='text-grey-500 text-center py-8'>
                  No booking pattern data yet
                </p>
              )}
            </CardBox>
          </div>

          {/* Recent Plans */}
          <CardBox>
            <div className='flex items-center justify-between mb-4'>
              <div className='flex items-center gap-3'>
                <div className='p-2 rounded-full bg-grey-50'>
                  <Icon
                    path={mdiTrendingUp}
                    size={20}
                    className='text-grey-500'
                  />
                </div>
                <h3 className='text-lg font-semibold text-grey-900'>
                  Recent Plans
                </h3>
              </div>
            </div>
            {isLoading ? (
              <div className='flex items-center justify-center py-8'>
                <Icon
                  path={mdiLoading}
                  size={24}
                  className='text-grey-400 animate-spin'
                />
              </div>
            ) : data?.recentPlans && data.recentPlans.length > 0 ? (
              <div className='space-y-3'>
                {data.recentPlans.slice(0, 5).map(plan => (
                  <div
                    key={plan.id}
                    className='flex items-center justify-between py-3 border-b border-grey-50 last:border-b-0'
                  >
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center gap-2'>
                        <Link
                          to={`/users/${plan.user.id}`}
                          className='font-medium text-grey-900 hover:text-blue-600 truncate'
                        >
                          {plan.user.firstName} {plan.user.lastName}
                        </Link>
                        <span className='text-grey-400'>→</span>
                        <Link
                          to={`/businesses/${plan.business.id}`}
                          className='text-grey-600 hover:text-blue-600 truncate'
                        >
                          {plan.business.name}
                        </Link>
                      </div>
                      <p className='text-sm text-grey-500'>
                        {formatDate(plan.startAt)}
                      </p>
                    </div>
                    <div className='flex items-center gap-3 ml-4'>
                      <PlanStateBadge state={plan.state} />
                      <span className='text-xs text-grey-400'>
                        {formatTimeAgo(plan.createdAt)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className='text-grey-500 text-center py-8'>No plans yet</p>
            )}
          </CardBox>
        </div>
      </ProtectedRoute>
    </DashboardLayout>
  );
};
